import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Share2 } from "lucide-react";
import { toast } from "sonner";
import { SocialLinks } from "../SocialLinks";
import { SOCIAL_LINKS_LIVE } from "../../data/content";

const SHARE_TEXT =
  "I just joined the waitlist for My Rich Aunt, a nonprofit making tax-deductible grants to verified college students. Take a look:";

const share = async () => {
  const url = window.location.origin;
  if (navigator.share) {
    try {
      await navigator.share({ title: "My Rich Aunt", text: SHARE_TEXT, url });
      return;
    } catch { /* user dismissed the sheet */ }
  }
  await navigator.clipboard.writeText(`${SHARE_TEXT} ${url}`);
  toast.success("Copied. Paste it anywhere a friend will see it.");
};

const NextLink = ({ to, children, testId }) => (
  <Link
    to={to}
    data-testid={testId}
    className="group inline-flex items-center gap-1.5 text-base font-semibold text-ink transition-colors hover:text-tangerine"
  >
    {children}
    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" strokeWidth={2} />
  </Link>
);

/** Post-submit state for the contact form: confirmation + "while you wait" next steps. */
export const ContactSuccess = ({ result }) => {
  const sent = !!result?.message_sent;
  const already = !!result?.already_signed_up;
  const notify = !!result?.notify;

  const heading = sent ? "Your note is on its way." : already ? "You're already on the list." : "You're in.";
  const body = sent
    ? `Your note is with us, and we reply as soon as we can.${notify ? " We'll also write the moment student campaigns go live." : ""}`
    : already
      ? "We already have this email saved. We'll write the moment student campaigns go live."
      : "Thanks for backing us this early. We'll write the moment our first campaigns go live.";

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      className="flex flex-col items-start gap-5"
      data-testid="contact-success"
    >
      <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-grass/15 text-grass">
        <Check className="h-6 w-6" strokeWidth={2.4} />
      </span>
      <h2 className="font-serif text-3xl leading-tight text-ink" data-testid="contact-success-heading">{heading}</h2>
      <p className="max-w-md text-base leading-relaxed text-olive">{body}</p>

      <div className="mt-2 w-full border-t border-ink/10 pt-6" data-testid="contact-next-steps">
        <p className="text-[13px] font-semibold uppercase tracking-[0.22em] text-olive">While you wait</p>
        <div className="mt-4 flex flex-col gap-3">
          <NextLink to="/preview" testId="contact-next-preview">Preview the platform</NextLink>
          <NextLink to="/#students" testId="contact-next-students">Meet the students</NextLink>
        </div>
        <div className="mt-6 flex flex-wrap items-center gap-4">
          <button
            type="button"
            onClick={share}
            data-testid="contact-share-button"
            className="inline-flex items-center gap-2 rounded-full border-2 border-ink px-5 py-2.5 text-base font-semibold text-ink transition-colors hover:bg-ink hover:text-sand"
          >
            <Share2 className="h-4 w-4" strokeWidth={2} />
            Share with a friend
          </button>
          {SOCIAL_LINKS_LIVE && (
            <div className="flex items-center gap-3">
              <span className="text-[13px] text-olive">Follow along</span>
              <SocialLinks size="sm" />
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};
